import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Home, Moon, Trophy, Users, Settings2, BookOpen, Gamepad2, Sun, Sparkles } from "lucide-react";
import { Layout } from "@/components/Layout";
import "./aurora.css";
import { usePatientMode } from "@/lib/patientMode";
import { loadState, saveState, totalXp, spentXp, levelOf, streakOf, Flame, coffreInfo } from "./scShared";
import ScAccueil from "./ScAccueil";
import ScMission from "./ScMission";
import ScTableau from "./ScTableau";
import ScConseil from "./ScConseil";
import ScPersonnaliser from "./ScPersonnaliser";
import ScGuide from "./ScGuide";
import { useEveningReminder } from "./ScRappel";
import NeuroQuest, { nqAvailable } from "./nq/NeuroQuest";

const TABS = [
  { id: "accueil", label: "Le jeu", Icon: Home },
  { id: "mission", label: "Mission du soir", Icon: Moon },
  { id: "tableau", label: "XP & récompenses", Icon: Trophy },
  { id: "conseil", label: "Conseil du dimanche", Icon: Users },
  { id: "neuroquest", label: "Neuro Quest", Icon: Gamepad2 },
  { id: "guide", label: "Guide parents", Icon: BookOpen },
  { id: "perso", label: "Personnaliser", Icon: Settings2 },
];

const NIGHT_KEY = "m2hat_sc_night";

export default function SuperCerveauModule() {
  const { patientMode } = usePatientMode();
  const [state, setState] = useState(loadState);
  const [tab, setTab] = useState(() => (loadState().setup.started ? "mission" : "accueil"));
  const [nightPref, setNightPref] = useState(() => localStorage.getItem(NIGHT_KEY) || "auto");
  useEffect(() => saveState(state), [state]);
  useEffect(() => localStorage.setItem(NIGHT_KEY, nightPref), [nightPref]);
  const night = nightPref === "auto" && tab === "mission";

  const update = useCallback((fn) => setState((s) => (typeof fn === "function" ? fn(s) : { ...s, ...fn })), []);
  const xp = useMemo(() => totalXp(state), [state]);
  const dispo = xp.enfant - spentXp(state);
  const go = useCallback((t) => { setTab(t); window.scrollTo({ top: 0, behavior: "smooth" }); }, []);
  const openMission = useCallback(() => go("mission"), [go]);
  useEveningReminder(state, update, openMission);

  const screens = {
    accueil: <ScAccueil state={state} update={update} onStart={() => go("mission")} onGuide={() => go("guide")} onReminder={() => go("perso")} />,
    mission: <ScMission state={state} update={update} />,
    tableau: <ScTableau state={state} update={update} xp={xp} dispo={dispo} />,
    conseil: <ScConseil state={state} update={update} onNeuroQuest={() => go("neuroquest")} />,
    neuroquest: <NeuroQuest state={state} update={update} onGoConseil={() => go("conseil")} />,
    guide: <ScGuide />,
    perso: <ScPersonnaliser state={state} update={update} />,
  };

  return (
    <Layout wide className={`sc-aurora ${night ? "sc-night" : ""}`}>
      <div className="animate-fade-up" data-testid="super-cerveau-module" data-night={night}>
        {!patientMode && (
          <Link to="/" className="text-sm text-[var(--gold)] hover:underline no-print" data-testid="sc-back-home">← Retour à l'accueil</Link>
        )}
        <div className="mt-4 mb-6 flex flex-wrap items-center justify-between gap-3 no-print">
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.3em] text-[var(--gold)]"><Sparkles size={12} /> Navigation céleste</div>
            <h1 className="font-serif-title text-3xl sm:text-4xl text-[var(--ink)] leading-none mt-1">Mission <span className="sc-gradient-text">Super-Cerveau</span></h1>
            <p className="text-xs sm:text-sm text-[var(--ink-soft)] mt-1.5">Le jeu coopératif enfant-parent pour gagner en autonomie</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {state.setup.started && (
              <>
                <Flame streak={streakOf(state.days)} testid="sc-streak-header" />
                <div className="flex items-center gap-2 rounded-full bg-[#FFF3D6] border-2 border-[#F2B84B] px-4 py-2 text-sm font-bold text-[#B8781A]" data-testid="sc-xp-header">
                  ⭐ Niveau {levelOf(xp.enfant)} · {xp.enfant} XP
                  {state.setup.childName && <span className="font-medium text-[var(--ink-soft)]">· {state.setup.childName}</span>}
                </div>
              </>
            )}
            {tab === "mission" && (
              <button onClick={() => setNightPref((p) => (p === "auto" ? "off" : "auto"))} data-testid="sc-night-toggle" aria-pressed={night}
                title={night ? "Nuit aurorale (moins de lumière bleue avant le coucher) — cliquer pour repasser en clair" : "Activer la nuit aurorale pendant la mission du soir"}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-white/70 px-3.5 py-2 text-xs font-semibold text-[var(--ink)] transition-colors">
                {night ? <Sun size={14} /> : <Moon size={14} />} {night ? "Mode clair" : "Mode nuit"}
              </button>
            )}
          </div>
        </div>
        <nav className="sc-aurabar inline-flex max-w-full gap-1 overflow-x-auto rounded-full p-1.5 mb-6 no-print" data-testid="sc-tabs" role="tablist">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => go(id)} data-testid={`sc-tab-${id}`} role="tab" aria-selected={tab === id}
              className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === id ? "text-white" : "text-[var(--ink)] hover:bg-white/70"}`}>
              <Icon size={16} /> {label}
              {id === "tableau" && coffreInfo(state).available && <span className="ml-1 text-sm" data-testid="sc-coffre-tab-badge">🎁</span>}
              {id === "neuroquest" && nqAvailable(state) > 0 && <span className="ml-1 w-5 h-5 rounded-full bg-[var(--gold-light)] text-white text-[10px] font-bold flex items-center justify-center" data-testid="sc-nq-badge">{nqAvailable(state)}</span>}
            </button>
          ))}
        </nav>
        {screens[tab]}
      </div>
    </Layout>
  );
}
